import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, Form, Grid, Text } from '@factorialco/deprecated-design-system'
import { F0DatePicker, F0Dialog, F0Select, F0SelectItemProps } from '@factorialco/f0-react'
import { Briefcase } from '@factorialco/f0-react/icons/app'
import {
  DatePickerValue,
  Input,
  NumberInput,
  Select,
} from '@factorialco/f0-react/dist/experimental'
import {
  AtsContractTypesEnum,
  AtsCreateVacancyDocument,
  AtsJobPostingSalaryFormatEnum,
  AtsJobPostingSalaryPeriodEnum,
  AtsJobPostingScheduleTypesEnum,
  AtsVacancyCreationFormDocument,
  AtsVacanciesTableDocument,
  AtsVacancyTypesEnum,
  AtsWorkplaceTypesEnum,
  ContractTypeFragment,
} from 'generated/resources/graphql'
import { groupBy } from 'lodash'

import { JobCatalogRoleSelector } from 'modules/ats/components/job_catalog/JobCatalogRoleSelector'
import { statusLabel } from 'modules/ats/components/JobStatusTag'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { i18nDateToString, stringToDate } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { EmployeeSelectorV2 } from 'modules/employees/components/EmployeeSelectorV2'

import { VacancyCreationSkeleton } from './Skeleton'

interface VacancyFormValues {
  name: string
  quantity: number | null
  recruiterId: number | null
  lineManagerId: number | null
  jobPostingId: string | null
  jobCatalogRoleUuid: string | null
  jobCatalogLevelUuid: string | null
  locationId: string
  teamId: string
  contractType: AtsContractTypesEnum | null
  scheduleType: AtsJobPostingScheduleTypesEnum | null
  workplaceType: AtsWorkplaceTypesEnum | undefined
  vacancyType: AtsVacancyTypesEnum | null
  deadline: string
  salaryCurrency: string
  salaryPeriod: AtsJobPostingSalaryPeriodEnum | null
  salaryFormat: AtsJobPostingSalaryFormatEnum | null
  salaryFromAmountInCents: number | null
  salaryToAmountInCents: number | null
}

export const VacancyCreationPage = () => {
  const { company } = useCurrentSession()
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const { loading, data } = useQuery(AtsVacancyCreationFormDocument)

  const [createVacancy] = useMutation(AtsCreateVacancyDocument, {
    extractorFn: (result) => result.atsMutations.createAtsVacancy,
    refetchQueries: [{ query: AtsVacanciesTableDocument }],
  })

  const locations = data?.locations.locations ?? []
  const teams = data?.teams.teams ?? []
  const contractTypes = (data?.ats.contractTypesConnection.nodes ?? []).filter(
    (type): type is ContractTypeFragment => Boolean(type?.label)
  )
  const jobPostings = data?.ats.jobPostingsConnection.nodes ?? []
  const jobCatalogRoles = data?.jobCatalog.treeNodesConnection.nodes ?? []
  const jobCatalogLevels = data?.jobCatalog.treeLevelsConnection.nodes ?? []
  const currencies = data?.apiCore?.currenciesConnection?.edges ?? []

  const jobCatalogRolesByUuid = useMemo(
    () => new Map(jobCatalogRoles.map((r) => [r.uuid, r.name ?? ''])),
    [jobCatalogRoles]
  )

  const jobCatalogLevelsByUuid = useMemo(
    () => new Map(jobCatalogLevels.map((l) => [l.uuid, l.name ?? ''])),
    [jobCatalogLevels]
  )

  const currencyOptions = useMemo(
    () =>
      [...currencies]
        .map((c) => ({ label: c.node.isoCode, value: c.node.isoCode }))
        .sort((a, b) => a.value.localeCompare(b.value)),
    [currencies]
  )

  const defaultCurrency = useMemo(() => {
    const companyCurrency = company?.currency
    const fromCompany =
      companyCurrency && currencyOptions.some((opt) => opt.value === companyCurrency)
        ? companyCurrency
        : null
    const fromPriority = currencies.find((c) => c.node.priority === 1)?.node.isoCode
    return fromCompany ?? fromPriority ?? currencyOptions[0]?.value ?? 'EUR'
  }, [company?.currency, currencies, currencyOptions])

  const form = useForm<VacancyFormValues>({
    defaultValues: {
      name: '',
      quantity: null,
      recruiterId: null,
      lineManagerId: null,
      jobPostingId: null,
      jobCatalogRoleUuid: null,
      jobCatalogLevelUuid: null,
      locationId: '',
      teamId: '',
      contractType: null,
      scheduleType: null,
      workplaceType: undefined,
      vacancyType: null,
      deadline: '',
      salaryCurrency: '',
      salaryPeriod: null,
      salaryFormat: null,
      salaryFromAmountInCents: null,
      salaryToAmountInCents: null,
    },
    validateOnChange: false,
    validateOnBlur: false,
    validate(values) {
      const errors: FormErrors<VacancyFormValues> = {}
      if (!values.name?.trim()) {
        errors.name = i18n.t('common.validations.empty')
      }
      if (!values.lineManagerId) {
        errors.lineManagerId = i18n.t('common.validations.empty')
      }
      if (!values.quantity || values.quantity < 1) {
        errors.quantity = i18n.t('ats.jobs.invalid_number_of_vacancies')
      }
      if (!values.locationId) {
        errors.locationId = i18n.t('common.validations.empty')
      }
      if (!values.contractType) {
        errors.contractType = i18n.t('common.validations.empty')
      }
      if (!values.scheduleType) {
        errors.scheduleType = i18n.t('common.validations.empty')
      }
      if (values.workplaceType === undefined) {
        errors.workplaceType = i18n.t('common.validations.empty')
      }
      if (!values.teamId) {
        errors.teamId = i18n.t('common.validations.empty')
      }
      if (!values.vacancyType) {
        errors.vacancyType = i18n.t('common.validations.empty')
      }
      if (!values.deadline) {
        errors.deadline = i18n.t('common.validations.empty')
      }
      if (!values.salaryCurrency) {
        errors.salaryCurrency = i18n.t('common.validations.empty')
      }
      if (!values.salaryPeriod) {
        errors.salaryPeriod = i18n.t('common.validations.empty')
      }
      if (!values.salaryFormat) {
        errors.salaryFormat = i18n.t('common.validations.empty')
      }
      if (values.salaryFromAmountInCents == null) {
        errors.salaryFromAmountInCents = i18n.t('common.validations.empty')
      }
      if (values.salaryFormat === 'range' && values.salaryToAmountInCents == null) {
        errors.salaryToAmountInCents = i18n.t('common.validations.empty')
      }
      if (
        values.salaryFormat === 'range' &&
        values.salaryToAmountInCents != null &&
        values.salaryFromAmountInCents != null &&
        values.salaryToAmountInCents < values.salaryFromAmountInCents
      ) {
        errors.salaryToAmountInCents = i18n.t('ats.vacancies.creation.range_error')
      }

      return errors
    },
    async onSubmit(values, { setErrors }) {
      const result = await createVacancy({
        variables: {
          companyId: company.id,
          name: values.name.trim(),
          lineManagerId: values.lineManagerId ?? 0,
          teamId: Number(values.teamId),
          locationId: Number(values.locationId),
          vacancyType: values.vacancyType,
          originType: 'manual',
          contractType: values.contractType,
          scheduleType: values.scheduleType,
          workplaceType: values.workplaceType,
          salaryPeriod: values.salaryPeriod,
          salaryType: values.salaryFormat,
          deadline: values.deadline,
          jobCatalogTreeRoleUuid: values.jobCatalogRoleUuid ?? undefined,
          jobCatalogTreeLevelUuid: values.jobCatalogLevelUuid ?? undefined,
          salaryCurrency: values.salaryCurrency,
          salaryFromAmountInCents: values.salaryFromAmountInCents,
          salaryToAmountInCents: values.salaryToAmountInCents,
          // quantity: values.quantity ?? 0, TODO: review!!!
        },
      })

      const handled = handleResult(result, (err) => setErrors(err))

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })
        navigate(navigation.ats.hiringPlan.vacancies.fullPath())
      }

      return handled
    },
  })

  const onClose = () => navigate(navigation.ats.hiringPlan.vacancies.fullPath())

  const contractTypeOptions = contractTypes.map(({ id, label, country }) => ({
    label: label ?? '',
    country: country ?? undefined,
    value: id,
  }))

  const contractTypeSelectOptions = useMemo((): F0SelectItemProps<string>[] => {
    const grouped = groupBy(contractTypeOptions, (item) => item.country ?? 'all')
    const options: F0SelectItemProps<string>[] = []
    Object.entries(grouped).forEach(([countryKey, items], index) => {
      if (index > 0) options.push({ type: 'separator' })
      options.push({
        value: countryKey,
        label: countryKey,
        disabled: true,
      })
      items
        .filter((i) => i.label)
        .sort((a, b) => a.label.localeCompare(b.label))
        .forEach(({ value, label }) => options.push({ value, label }))
    })
    return options
  }, [contractTypeOptions])

  const locationOptions = useMemo(
    () =>
      [...locations]
        .filter((loc) => loc?.name)
        .sort((a, b) => (a?.name ?? '').localeCompare(b?.name ?? ''))
        .map((loc) => ({ label: loc?.name ?? '', value: String(loc?.id ?? '') })),
    [locations]
  )

  const teamOptions = useMemo(
    () =>
      [...teams]
        .filter((t) => t?.name)
        .sort((a, b) => (a?.name ?? '').localeCompare(b?.name ?? ''))
        .map((t) => ({ label: t?.name ?? '', value: String(t?.id ?? '') })),
    [teams]
  )

  const jobPostingOptions: F0SelectItemProps<string>[] = useMemo(
    () =>
      jobPostings.map((jp) => {
        const descriptionParts = [
          [jp.location?.name, jp.workplaceTypeLabel ? `(${jp.workplaceTypeLabel})` : null]
            .filter(Boolean)
            .join(' '),
          jp.jobCatalogNodeLevelsUuid
            ? jobCatalogLevelsByUuid.get(jp.jobCatalogNodeLevelsUuid)
            : jp.jobCatalogNodeRolesUuid
              ? jobCatalogRolesByUuid.get(jp.jobCatalogNodeRolesUuid)
              : undefined,
          jp.team?.name,
        ].filter(Boolean)

        return {
          label: jp.title ?? '',
          value: String(jp.id),
          icon: Briefcase,
          tag: statusLabel(jp.status),
          description: descriptionParts.length > 0 ? descriptionParts.join(' / ') : undefined,
        }
      }),
    [jobPostings, jobCatalogRolesByUuid, jobCatalogLevelsByUuid]
  )

  const deadlinePickerValue = useMemo(() => {
    if (!form.values.deadline) return undefined
    const parsed = stringToDate(form.values.deadline, 'backend')
    if (!parsed.isValid()) return undefined
    const d = parsed.toDate()
    return {
      granularity: 'day' as const,
      value: { from: d, to: d },
    }
  }, [form.values.deadline])

  const handleDeadlineChange = (date: DatePickerValue | undefined) => {
    const from = date?.value?.from
    form.setFieldValue('deadline', from ? i18nDateToString(from, 'backend') : '')
  }

  useEffect(() => {
    if (defaultCurrency && !form.values.salaryCurrency) {
      form.setFieldValue('salaryCurrency', defaultCurrency)
    }
  }, [defaultCurrency, form.values.salaryCurrency])

  return (
    <F0Dialog
      title={i18n.t('ats.vacancies.creation.title')}
      isOpen
      onClose={onClose}
      primaryAction={{
        label: i18n.t('ats.vacancies.creation.create_button'),
        onClick: () => form.handleSubmit(),
        loading: form.isSubmitting,
      }}
      secondaryAction={{
        label: i18n.t('common.cancel'),
        onClick: onClose,
      }}
    >
      {loading ? (
        <VacancyCreationSkeleton />
      ) : (
        <Form padding={null}>
          <Box width="full" gap="s16">
            <Text size="200" weight="semibold">
              {i18n.t('ats.vacancies.creation.basic_info_title')}
            </Text>
            <Grid variant="fixed">
              <Box gridColSpan={{ xxs: 1, xs: 6 }}>
                <Input
                  label={i18n.t('ats.vacancies.creation.vacancy_name')}
                  name="vacancyName"
                  placeholder={i18n.t('ats.vacancies.creation.vacancy_name_placeholder')}
                  size="md"
                  value={form.values.name}
                  onChange={(value) => form.setFieldValue('name', value)}
                  error={form.errors.name}
                  required
                />
              </Box>
              <Box gridColSpan={{ xxs: 1, xs: 6 }}>
                <NumberInput
                  label={i18n.t('ats.vacancies.creation.quantity')}
                  name="quantity"
                  locale="en-US"
                  value={form.values.quantity}
                  onChange={(v) => form.setFieldValue('quantity', v === null ? null : Number(v))}
                  placeholder={i18n.t('ats.vacancies.creation.quantity_placeholder')}
                  size="md"
                  error={form.errors.quantity}
                  required
                />
              </Box>
              <Box gridColSpan={{ xxs: 1, xs: 12 }}>
                <EmployeeSelectorV2
                  label={i18n.t('ats.vacancies.creation.line_manager')}
                  mandatory
                  error={form.errors.lineManagerId}
                  value={form.values.lineManagerId ?? undefined}
                  onChange={(id) => form.setFieldValue('lineManagerId', id ?? null)}
                />
              </Box>
            </Grid>
            <Grid variant="fixed">
              <Box gridColSpan={{ xxs: 1, xs: 12 }}>
                <F0Select
                  label={i18n.t('ats.vacancies.creation.job_opening')}
                  name="jobOpening"
                  placeholder={i18n.t('common.select')}
                  size="md"
                  value={form.values.jobPostingId ?? undefined}
                  onChange={(value: string) => form.setFieldValue('jobPostingId', value)}
                  options={jobPostingOptions}
                  showSearchBox
                  searchBoxPlaceholder={i18n.t(
                    'ats.vacancies.creation.job_opening_search_placeholder'
                  )}
                  searchEmptyMessage={i18n.t('ats.vacancies.creation.search_no_results_found')}
                />
              </Box>
            </Grid>

            <Text size="200" weight="semibold">
              {i18n.t('ats.vacancies.creation.role_details_title')}
            </Text>
            {jobCatalogRoles.length > 0 && (
              <Grid variant="fixed">
                <Box gridColSpan={{ xxs: 1, xs: 12 }}>
                  <JobCatalogRoleSelector
                    jobCatalogRoles={jobCatalogRoles}
                    selectedRoleUuid={form.values.jobCatalogRoleUuid ?? undefined}
                    selectedRoleLevelUuid={form.values.jobCatalogLevelUuid ?? undefined}
                    onLevelSelect={(roleUuid, roleLevelUuid) => {
                      form.setValues({
                        ...form.values,
                        jobCatalogRoleUuid: roleUuid ?? null,
                        jobCatalogLevelUuid: roleLevelUuid ?? null,
                      })
                    }}
                  />
                </Box>
              </Grid>
            )}
            <Grid variant="fixed">
              <Box gridColSpan={{ xxs: 1, xs: 6 }}>
                <F0Select
                  label={i18n.t('ats.vacancies.creation.workplace')}
                  name="workplace"
                  placeholder={i18n.t('common.select')}
                  size="md"
                  value={form.values.locationId || undefined}
                  onChange={(value: string) => form.setFieldValue('locationId', value)}
                  options={locationOptions}
                  showSearchBox
                  searchBoxPlaceholder={i18n.t(
                    'ats.vacancies.creation.workplace_search_placeholder'
                  )}
                  searchEmptyMessage={i18n.t('ats.vacancies.creation.search_no_results_found')}
                  error={form.errors.locationId}
                  required
                />
              </Box>
              <Box gridColSpan={{ xxs: 1, xs: 6 }}>
                <F0Select
                  label={i18n.t('ats.vacancies.creation.team')}
                  name="team"
                  placeholder={i18n.t('common.select')}
                  size="md"
                  value={form.values.teamId || undefined}
                  onChange={(value: string) => form.setFieldValue('teamId', value)}
                  options={teamOptions}
                  showSearchBox
                  searchBoxPlaceholder={i18n.t('ats.vacancies.creation.team_search_placeholder')}
                  searchEmptyMessage={i18n.t('ats.vacancies.creation.search_no_results_found')}
                  error={form.errors.teamId}
                  required
                />
              </Box>
            </Grid>
            <Grid variant="fixed">
              <Box gridColSpan={{ xxs: 1, xs: 6 }}>
                <F0Select
                  label={i18n.t('ats.vacancies.creation.agreement_type')}
                  name="contractType"
                  placeholder={i18n.t('common.select')}
                  size="md"
                  value={form.values.contractType ?? undefined}
                  onChange={(value: string) => form.setFieldValue('contractType', value || null)}
                  options={contractTypeSelectOptions}
                  showSearchBox
                  searchEmptyMessage={i18n.t('ats.vacancies.creation.search_no_results_found')}
                  error={form.errors.contractType}
                  required
                />
              </Box>
              <Box gridColSpan={{ xxs: 1, xs: 6 }}>
                <Select
                  label={i18n.t('ats.vacancies.creation.schedule_type')}
                  name="scheduleType"
                  placeholder={i18n.t('common.select')}
                  size="md"
                  value={form.values.scheduleType ?? undefined}
                  options={[
                    { label: i18n.t('ats.job_schedule.full_time'), value: 'full_time' },
                    { label: i18n.t('ats.job_schedule.part_time'), value: 'part_time' },
                  ]}
                  onChange={(value: string) => form.setFieldValue('scheduleType', value || null)}
                  error={form.errors.scheduleType}
                  required
                />
              </Box>
            </Grid>
            <Grid variant="fixed">
              <Box gridColSpan={{ xxs: 1, xs: 12 }}>
                <F0Select
                  label={i18n.t('ats.vacancies.creation.type_of_location')}
                  name="workplaceType"
                  placeholder={i18n.t('common.select')}
                  size="md"
                  value={form.values.workplaceType ?? undefined}
                  onChange={(value: string) =>
                    form.setFieldValue('workplaceType', value || undefined)
                  }
                  options={[
                    { label: i18n.t('ats.workplace_types.onsite'), value: 'onsite' },
                    { label: i18n.t('ats.workplace_types.hybrid'), value: 'hybrid' },
                    { label: i18n.t('ats.workplace_types.remote'), value: 'remote' },
                  ]}
                  error={form.errors.workplaceType}
                  required
                />
              </Box>
            </Grid>
            <Grid variant="fixed">
              <Box gridColSpan={{ xxs: 1, xs: 6 }}>
                <F0Select
                  label={i18n.t('ats.vacancies.creation.vacancy_type')}
                  name="vacancyType"
                  placeholder={i18n.t('common.select')}
                  size="md"
                  value={form.values.vacancyType ?? undefined}
                  onChange={(value: string) => form.setFieldValue('vacancyType', value || null)}
                  options={[
                    {
                      label: i18n.t('ats.vacancies.table.vacancy_type.new_position'),
                      value: 'new_position',
                    },
                    {
                      label: i18n.t('ats.vacancies.table.vacancy_type.backfill'),
                      value: 'backfill',
                    },
                  ]}
                  error={form.errors.vacancyType}
                  required
                />
              </Box>
              <Box gridColSpan={{ xxs: 1, xs: 6 }}>
                <F0DatePicker
                  label={i18n.t('ats.vacancies.creation.deadline')}
                  size="md"
                  value={deadlinePickerValue}
                  onChange={handleDeadlineChange}
                  placeholder={i18n.t('ats.vacancies.creation.start_date_placeholder')}
                  error={form.errors.deadline}
                  required
                />
              </Box>
            </Grid>

            <Text size="200" weight="semibold">
              {i18n.t('ats.vacancies.creation.salary_details')}
            </Text>
            <Grid variant="fixed">
              <Box gridColSpan={{ xxs: 1, xs: 4 }}>
                <Select
                  label={i18n.t('ats.vacancies.creation.salary_period_label')}
                  name="salaryPeriod"
                  placeholder={i18n.t('common.select')}
                  size="md"
                  value={form.values.salaryPeriod ?? undefined}
                  options={[
                    {
                      label: i18n.t('ats.vacancies.creation.salary_period_annual'),
                      value: 'annual',
                    },
                    {
                      label: i18n.t('ats.vacancies.creation.salary_period_monthly'),
                      value: 'monthly',
                    },
                    { label: i18n.t('ats.vacancies.creation.salary_period_daily'), value: 'daily' },
                  ]}
                  onChange={(value: string) => form.setFieldValue('salaryPeriod', value)}
                  error={form.errors.salaryPeriod}
                  required
                />
              </Box>
              <Box gridColSpan={{ xxs: 1, xs: 4 }}>
                <Select
                  label={i18n.t('ats.vacancies.creation.salary_format_label')}
                  name="salaryFormat"
                  placeholder={i18n.t('common.select')}
                  size="md"
                  value={form.values.salaryFormat ?? undefined}
                  options={[
                    { label: i18n.t('ats.vacancies.creation.salary_format_range'), value: 'range' },
                    {
                      label: i18n.t('ats.vacancies.creation.salary_format_fixed_amount'),
                      value: 'fixed_amount',
                    },
                  ]}
                  onChange={(value: string) => form.setFieldValue('salaryFormat', value)}
                  error={form.errors.salaryFormat}
                  required
                />
              </Box>
              <Box gridColSpan={{ xxs: 1, xs: 4 }}>
                <F0Select
                  label={i18n.t('ats.vacancies.creation.currency')}
                  name="currency"
                  placeholder={i18n.t('common.select')}
                  size="md"
                  value={form.values.salaryCurrency ?? defaultCurrency ?? undefined}
                  onChange={(value: string | undefined) =>
                    form.setFieldValue('salaryCurrency', value)
                  }
                  showSearchBox
                  searchEmptyMessage={i18n.t('ats.vacancies.creation.search_no_results_found')}
                  options={currencyOptions}
                  error={form.errors.salaryCurrency}
                  required
                />
              </Box>
            </Grid>
            <Grid variant="fixed">
              <Box gridColSpan={{ xxs: 1, xs: 12 }}>
                {form.values.salaryFormat === 'range' ? (
                  <Grid variant="fixed">
                    <Box gridColSpan={{ xxs: 1, xs: 6 }}>
                      <NumberInput
                        label={i18n.t('ats.vacancies.creation.salary_from_amount_range')}
                        name="salaryFrom"
                        locale={i18n.locale}
                        placeholder={i18n.t('ats.vacancies.creation.amount_placeholder')}
                        size="md"
                        value={form.values.salaryFromAmountInCents}
                        onChange={(v: number | null) =>
                          form.setFieldValue(
                            'salaryFromAmountInCents',
                            v === null ? null : Number(v)
                          )
                        }
                        units={form.values.salaryCurrency}
                        error={form.errors.salaryFromAmountInCents}
                        required
                      />
                    </Box>
                    <Box gridColSpan={{ xxs: 1, xs: 6 }}>
                      <NumberInput
                        label={i18n.t('ats.vacancies.creation.salary_to_amount')}
                        name="salaryTo"
                        locale={i18n.locale}
                        placeholder={i18n.t('ats.vacancies.creation.amount_placeholder')}
                        size="md"
                        value={form.values.salaryToAmountInCents}
                        onChange={(v: number | null) =>
                          form.setFieldValue('salaryToAmountInCents', v === null ? null : Number(v))
                        }
                        units={form.values.salaryCurrency}
                        error={form.errors.salaryToAmountInCents}
                        required
                      />
                    </Box>
                  </Grid>
                ) : (
                  <NumberInput
                    label={i18n.t('ats.vacancies.creation.salary_from_amount')}
                    name="salaryFrom"
                    locale={i18n.locale}
                    placeholder={i18n.t('ats.vacancies.creation.amount_placeholder')}
                    size="md"
                    value={form.values.salaryFromAmountInCents}
                    onChange={(v: number | null) =>
                      form.setFieldValue('salaryFromAmountInCents', v === null ? null : Number(v))
                    }
                    units={form.values.salaryCurrency}
                    error={form.errors.salaryFromAmountInCents}
                    required
                  />
                )}
              </Box>
            </Grid>
          </Box>
        </Form>
      )}
    </F0Dialog>
  )
}

export default VacancyCreationPage
